<template>
  <div class="work-page">
    <div class="container">
      <h1 class="page-title">CTO, INFRASTRUCTURE, AND AI LEADER</h1>

      <div class="bio">
        <p>
          I build and scale the infrastructure that powers high-growth companies.
        </p>
        <p>
          With 15+ years in systems engineering, cloud architecture, and DevOps
          leadership, I specialize in taking companies from zero to production-ready
          — building teams, architecting AWS platforms, and establishing the
          operational foundations that let engineering organizations move fast
          and ship reliably.
        </p>
        <p>
          I also have a passion for learning and optimizing systems, processes, and workflows.
          The last few years have been a whirlwind of innovation and discovery in the world of AI and automation.
          I help companies run agents, at scale, to perform work never before possible.
        </p>
        <p>
          Currently, I serve as CTO at
          <strong>Lendflow</strong>.
        </p>
      </div>

      <h2 class="section-title">EXPERIENCE</h2>

      <div class="timeline">
        <div class="timeline-item" v-for="job in jobs" :key="job.company + job.dates">
          <div class="timeline-header">
            <h3>{{ job.company }}</h3>
            <span class="timeline-date">{{ job.dates }}</span>
          </div>
          <div class="timeline-role">{{ job.title }}</div>
          <p class="timeline-description">{{ job.description }}</p>
        </div>
      </div>

      <h2 class="section-title">CONNECT</h2>

      <div class="links">
        <a href="https://www.linkedin.com/in/joshscurtis/" target="_blank" rel="noopener">
          LinkedIn
        </a>
        <a href="https://github.com/joshscurtis" target="_blank" rel="noopener">
          GitHub
        </a>
        <a href="mailto:josh@joshcurtis.com">
          Email
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WorkPage',
  data() {
    return {
      jobs: [
        {
          company: 'LENDFLOW (YC W21)',
          title: 'Chief Technology Officer',
          dates: 'April 2026 – Present',
          description: 'Promoted to CTO to lead engineering across product, platform, and infrastructure. Set technical strategy and drive AI and automation initiatives across the business.'
        },
        {
          company: 'LENDFLOW (YC W21)',
          title: 'Manager of DevOps & Infrastructure',
          dates: 'June 2021 – April 2026',
          description: 'First infrastructure hire. Built the DevOps function from zero. Owned all cloud infrastructure, CI/CD, observability, compliance, and data engineering. Partnered with CTO and CEO on AI and automation strategy.'
        },
        {
          company: 'AMERICAN CITY BUSINESS JOURNALS',
          title: 'Senior DevOps Engineer & Cloud Architect',
          dates: 'Feb 2018 – June 2021',
          description: 'Senior on a four-person team managing AWS infrastructure serving 1B+ web visits/year across multiple media properties.'
        },
        {
          company: 'COMMERCE SIGNALS (TRANSUNION)',
          title: 'DevOps Manager',
          dates: 'Jan 2017 – Feb 2018',
          description: 'Sole DevOps hire pre-Series B. Owned all infrastructure, CI/CD, and SOC2 compliance.'
        },
        {
          company: 'AMERICAN CITY BUSINESS JOURNALS',
          title: 'DevOps Engineer / Sr. Systems Administrator',
          dates: 'Dec 2010 – Jan 2017',
          description: '6+ year tenure from sysadmin to senior technical role. Led the full datacenter-to-AWS migration across all web properties.'
        }
      ],
      techStack: [
        'AWS', 'Terraform', 'Docker', 'Kubernetes', 'ECS',
        'Python', 'Bash', 'NodeJS', 'SQL',
        'LangChain', 'LangGraph', 'n8n', 'Jupyter',
        'Bitbucket Pipelines', 'GitHub Actions',
        'MySQL', 'PostgreSQL', 'Redis', 'OpenSearch', 'RedShift',
        'New Relic', 'Grafana', 'Datadog', 'CloudWatch',
        'SOC2', 'PCI-DSS', 'Ansible', 'CloudFormation'
      ]
    }
  }
}
</script>

<style scoped>
.work-page {
  padding-top: 125px;
  padding-bottom: 60px;
  color: #e0e0e0;
  min-height: 100vh;
}

@media (max-width: 768px) {
  .work-page {
    /* Extra top padding on mobile so title clears the taller fixed nav when tagline wraps */
    padding-top: 220px;
  }
}

.page-title {
  text-align: center;
  font-size: 2.75rem;
  font-weight: 700;
  letter-spacing: 4px;
  margin-bottom: 40px;
  color: #ffffff;
  font-family: 'Anton', sans-serif;
}

.section-title {
  font-size: 1.75rem;
  font-weight: 700;
  letter-spacing: 3px;
  margin-top: 50px;
  margin-bottom: 24px;
  padding-bottom: 8px;
  border-bottom: 2px solid #444;
  color: #ffffff;
  font-family: 'Anton', sans-serif;
}

.bio {
  max-width: 740px;
  margin: 0 auto 40px;
  font-size: 1.2rem;
  line-height: 1.7;
  color: #ccc;
}

.bio p {
  margin-bottom: 16px;
}

.bio strong {
  color: #ffffff;
}

.timeline-item {
  padding: 16px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-wrap: wrap;
}

.timeline-header h3 {
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: 1px;
  color: #ffffff;
  margin: 0;
}

.timeline-date {
  font-size: 1rem;
  color: #888;
}

.timeline-role {
  font-size: 1.1rem;
  font-style: italic;
  color: #bbb;
  margin: 4px 0 8px;
}

.timeline-description {
  font-size: 1.05rem;
  color: #999;
  margin: 0;
  line-height: 1.6;
}

.tech-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.tech-tag {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 1rem;
  color: #ccc;
  letter-spacing: 0.5px;
}

.links {
  display: flex;
  gap: 32px;
  margin-top: 8px;
}

.links a {
  color: #ccc;
  text-decoration: none;
  font-size: 1.2rem;
  letter-spacing: 1px;
  transition: color 0.2s;
}

.links a:hover {
  color: #ffffff;
}
</style>
